import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import styles from './styles.module.scss'; // Certifique-se de atualizar o SCSS abaixo
import { Board } from '../../../../../components/Board';
import { NavButton } from '../../../../../components/NavButton';
import { verifyRoutePermissions } from '../../../../../globalFunctions';
import { useNavigate } from 'react-router-dom';
import { useSchoolClass } from '../../../../../hooks/useSchoolClass';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/pt-br';

export const DataTeste = () => {
    const navigate = useNavigate();
    const { data: schoolClassesData, isLoading: isLoadingClasses } = useSchoolClass();
    
    // Estados
    const [user, setUser] = useState(null);
    const [testes, setTestes] = useState([]);
    const [isLoadingTests, setIsLoadingTests] = useState(false);
    const [isSending, setIsSending] = useState(false);

    // Estados do Formulário
    const [dataInicio, setDataInicio] = useState(null);
    const [dataFim, setDataFim] = useState(null);
    const [selectedTurmaId, setSelectedTurmaId] = useState('');

    // 1. Configuração Memoizada para evitar recriação desnecessária
    const requestConfig = useMemo(() => {
        if (!user?.token) return null;
        return {
            headers: {
                'Content-Type': 'application/json', // Geralmente JSON é preferível, mas mantive a lógica se seu back exigir form-url
                Authorization: `Bearer ${user.token}`,
                Accept: 'application/json'
            }
        };
    }, [user?.token]);

    // Verificação de permissão
    useEffect(() => {
        const validatedUser = verifyRoutePermissions(
            ['auth', 'email.verified', 'not.pending.update', 'logicmanager.classes'],
            navigate
        );
        if (validatedUser) setUser(validatedUser);
    }, [navigate]);

    // Inicializa a turma selecionada assim que as turmas carregarem
    useEffect(() => {
        if (schoolClassesData?.length > 0 && !selectedTurmaId) {
            setSelectedTurmaId(schoolClassesData[0].id);
        }
    }, [schoolClassesData, selectedTurmaId]);

    // 2. Fetch de Testes Otimizado
    const loadTests = useCallback(async () => {
        if (!requestConfig) return;

        setIsLoadingTests(true);
        try {
            const { data } = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/logicmanager/tests`,
                requestConfig
            );
            setTestes(data);
        } catch (error) {
            console.error("Erro ao carregar testes:", error);
        } finally {
            setIsLoadingTests(false);
        }
    }, [requestConfig]);

    // Carrega os testes apenas quando o usuário/config estiver pronto
    useEffect(() => {
        if (requestConfig) {
            loadTests();
        }
    }, [requestConfig, loadTests]);

    // Deletar Teste
    const deleteTest = useCallback(async (id) => {
        if (!window.confirm("Deseja realmente excluir este teste?")) return;
        
        try {
            await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/logicmanager/tests/${id}`,
                requestConfig
            );
            // Atualização otimista ou recarga
            setTestes(prev => prev.filter(t => t.testId !== id)); 
        } catch (error) {
            alert("Erro ao deletar teste");
        }
    }, [requestConfig]);

    // Enviar Formulário
    const sendForm = useCallback(async () => {
        if (!dataInicio || !dataFim || !selectedTurmaId) {
            alert('Por favor, preencha todos os campos (Turma, Início e Fim).');
            return;
        }

        setIsSending(true);
        try {
            await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/logicmanager/tests`,
                {
                    startDate: dataInicio.format('YYYY-MM-DD'),
                    endDate: dataFim.format('YYYY-MM-DD'),
                    fk_class_id: selectedTurmaId
                },
                requestConfig
            );
            
            alert('Teste agendado com sucesso!');
            // Limpar campos
            setDataInicio(null);
            setDataFim(null);
            loadTests(); // Recarrega a lista
        } catch (err) {
            console.error('Erro ao salvar:', err);
            const msg = err.response?.data?.message || 'Erro ao salvar datas.';
            alert(`❌ Erro: ${msg}`);
        } finally {
            setIsSending(false);
        }
    }, [dataInicio, dataFim, selectedTurmaId, requestConfig, loadTests]);

    const goBackwards = () => navigate('/manager');

    // Helper para encontrar nome da turma
    const getClassName = (id) => {
        const cls = schoolClassesData?.find(c => c.id == id); // == para cobrir string/int
        return cls ? cls.name : 'Turma desconhecida';
    };

    return (
        <div className={styles.wrapper}>
            <NavButton
                direction='left'
                style={{ left: '0.4vw', top: '2vw', zIndex: '20' }}
                onClick={goBackwards}
            />

            <Board wrapperClass={styles.board}>
                <div className={styles.container}>
                    <h1 className={styles.titulo}>
                        Agendamento de Testes
                    </h1>

                    {/* --- Área de Criação (Formulário) --- */}
                    <div className={styles.formCard}>
                        <h3>Novo Agendamento</h3>
                        <div className={styles.formRow}>
                            <div className={styles.inputGroup}>
                                <label>Turma</label>
                                <select 
                                    value={selectedTurmaId} 
                                    onChange={e => setSelectedTurmaId(e.target.value)}
                                    disabled={isLoadingClasses}
                                    className={styles.selectInput}
                                >
                                    {isLoadingClasses && <option>Carregando...</option>}
                                    {!isLoadingClasses && schoolClassesData?.map(turma => (
                                        <option key={turma.id} value={turma.id}>{turma.name}</option>
                                    ))}
                                </select>
                            </div>

                            <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale='pt-br'>
                                <DatePicker
                                    label='Data Início'
                                    value={dataInicio}
                                    onChange={setDataInicio}
                                    className={styles.datePicker}
                                    slotProps={{ textField: { size: 'small' } }}
                                />
                                <DatePicker
                                    label='Data Fim'
                                    value={dataFim}
                                    onChange={setDataFim}
                                    className={styles.datePicker}
                                    slotProps={{ textField: { size: 'small' } }}
                                />
                            </LocalizationProvider>

                            <button 
                                onClick={sendForm} 
                                disabled={isSending || isLoadingClasses}
                                className={styles.addButton}
                            >
                                {isSending ? 'Salvando...' : 'Adicionar'}
                            </button>
                        </div>
                    </div>

                    {/* --- Lista de Cards --- */}
                    <div className={styles.divider} />
                    
                    <h3>Testes Agendados</h3>
                    
                    {isLoadingTests ? (
                        <p className={styles.loadingText}>Carregando agendamentos...</p>
                    ) : (
                        <div className={styles.cardsGrid}>
                            {testes && testes.length > 0 ? (
                                testes.map((teste) => (
                                    <div key={teste.testId || Math.random()} className={styles.card}>
                                        <div className={styles.cardHeader}>
                                            <span className={styles.cardTitle}>
                                                {teste.name || 'Questionário'}
                                            </span>
                                            <button 
                                                onClick={() => deleteTest(teste.testId)}
                                                className={styles.deleteBtn}
                                                title="Excluir"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        
                                        <div className={styles.cardBody}>
                                            <div className={styles.dateInfo}>
                                                <small>Início</small>
                                                <strong>{moment(teste.startDate).format('DD/MM/YYYY')}</strong>
                                            </div>
                                            <div className={styles.arrow}>→</div>
                                            <div className={styles.dateInfo}>
                                                <small>Fim</small>
                                                <strong>{moment(teste.endDate).format('DD/MM/YYYY')}</strong>
                                            </div>
                                        </div>

                                        {/* Se o objeto teste tiver o ID da turma, podemos mostrar o nome */}
                                        {teste.fk_class_id && (
                                            <div className={styles.cardFooter}>
                                                Turma: {getClassName(teste.fk_class_id)}
                                            </div>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className={styles.emptyState}>Nenhum teste agendado encontrado.</p>
                            )}
                        </div>
                    )}
                </div>
            </Board>
        </div>
    );
};